import type { FacePose } from "@/lib/face-align";

import type { PairAlignment } from "@/types/photo-studio";

interface Size {
  width: number;
  height: number;
}

// Only translate, scale and rotate: the photos themselves are never warped.
const MIN_SCALE = 0.2;
const MAX_SCALE = 5;
const MAX_ROTATION_DEG = 45;

interface FramePose {
  x: number; // mid-eye point, px from the frame centre
  y: number;
  iod: number; // inter-ocular distance, px in the frame
  angle: number; // eye-line angle, degrees, clockwise positive (y down)
}

// A pose from detectPose is in fractions of the image's width and height, so
// its distance and angle are skewed for any non-square photo. Work out the
// real eye vector in pixels, then place it in a frame the photo is fitted to.
function toFramePose(
  pose: FacePose,
  image: Size,
  frame: Size,
  fit: "contain" | "cover",
): FramePose | null {
  if (!image.width || !image.height || !frame.width || !frame.height) {
    return null;
  }
  const radians = (pose.angle * Math.PI) / 180;
  const eyeX = pose.iod * Math.cos(radians) * image.width;
  const eyeY = pose.iod * Math.sin(radians) * image.height;
  const fitScale =
    fit === "contain"
      ? Math.min(frame.width / image.width, frame.height / image.height)
      : Math.max(frame.width / image.width, frame.height / image.height);
  return {
    x: (pose.x - 0.5) * image.width * fitScale,
    y: (pose.y - 0.5) * image.height * fitScale,
    iod: Math.hypot(eyeX, eyeY) * fitScale,
    angle: (Math.atan2(eyeY, eyeX) * 180) / Math.PI,
  };
}

/**
 * Moves the AFTER photo onto the BEFORE photo's face.
 *
 * Both photos are fitted into identical frames of size `frame` using `fit`.
 * The result transforms AFTER about the frame centre: `scale`, then
 * `rotationDeg` (clockwise positive, as CSS `rotate`), then a translate of
 * `dx * frame.width`, `dy * frame.height`. After it, AFTER's mid-eye point,
 * inter-ocular distance and eye-line angle match BEFORE's.
 *
 * Null when either pose is missing, or the fix would be implausibly large
 * (most likely a wrong face), so callers leave the photos as they are.
 */
export function alignPair(
  before: FacePose | null,
  beforeSize: Size,
  after: FacePose | null,
  afterSize: Size,
  frame: Size,
  fit: "contain" | "cover",
): PairAlignment | null {
  if (!before || !after) return null;
  const target = toFramePose(before, beforeSize, frame, fit);
  const source = toFramePose(after, afterSize, frame, fit);
  if (!target || !source || source.iod <= 0 || target.iod <= 0) return null;

  const scale = target.iod / source.iod;
  let rotationDeg = target.angle - source.angle;
  while (rotationDeg > 180) rotationDeg -= 360;
  while (rotationDeg < -180) rotationDeg += 360;
  if (
    !(scale > MIN_SCALE && scale < MAX_SCALE) ||
    Math.abs(rotationDeg) > MAX_ROTATION_DEG
  ) {
    return null;
  }

  // Where AFTER's mid-eye point lands after scale + rotate about the centre.
  const radians = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const movedX = scale * (source.x * cos - source.y * sin);
  const movedY = scale * (source.x * sin + source.y * cos);
  const dx = (target.x - movedX) / frame.width;
  const dy = (target.y - movedY) / frame.height;
  if (Math.abs(dx) > 1 || Math.abs(dy) > 1) return null;

  return { scale, rotationDeg, dx, dy };
}
